import type { IncomingInvoice, Invoice } from '@forq/finance-data';

/**
 * OP-Listen (Offene-Posten-Listen) im Stil der ARISTA-Vorlage: gruppiert je Debitor/Kreditor, mit
 * Spalten Buchungsdatum · Beleg · ext. Belegnummer · Beschreibung · Fälligkeit · (Mahnstufe) · Restbetrag,
 * Zwischensumme je Partner, Gesamtsumme und einer Fälligkeits-Staffel (nicht fällig / 1-40 / … / 181+).
 * Rein clientseitig gerendert (wie die übrigen FTW-Belege) und in eigenem Tab geöffnet → Strg+P druckt.
 */

export type OpListKind = 'debitor' | 'kreditor';

interface OpListItem {
  readonly bookingDate: string | null;
  readonly documentNumber: string;
  readonly externalNumber: string;
  readonly description: string;
  readonly dueDate: string | null;
  readonly dunningLevel: number;
  readonly openAmount: number;
}

interface OpListGroup {
  readonly partnerNumber: string;
  readonly partnerName: string;
  readonly partnerCity: string;
  readonly partnerPhone: string;
  readonly items: readonly OpListItem[];
  readonly subtotal: number;
}

export interface OpListPartnerContact {
  readonly city: string;
  readonly phone: string;
}
/** Stammdaten-Kontakte je Partnernummer (Ort/Telefon) — reichert die Gruppenköpfe an (wie in der ARISTA-Vorlage). */
export type OpListPartnerContacts = ReadonlyMap<string, OpListPartnerContact>;

interface AgingBucket {
  readonly label: string;
  readonly count: number;
  readonly amount: number;
}

export interface OpListData {
  readonly groups: readonly OpListGroup[];
  readonly grandTotal: number;
  readonly aging: readonly AgingBucket[];
}

const nf = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function money(value: number): string {
  return nf.format(value);
}
function dateShort(value: string | null): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}.${month}.${year.slice(2)}` : value;
}
function dunningText(level: number): string {
  return level > 0 ? String(level) : '';
}
/** Ort · Tel. für den Gruppenkopf (bereits escaped); leer, wenn keine Stammdaten vorliegen. */
function contactLine(city: string, phone: string): string {
  const bits = [city, phone ? `Tel. ${phone}` : ''].filter(Boolean).map(esc);
  return bits.length ? ` <span class="op-partner-contact">· ${bits.join(' · ')}</span>` : '';
}
function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Überfälligkeitstage zum Stichtag (>0 = überfällig, <=0 = noch nicht fällig). */
function overdueDays(dueDate: string | null, today: string): number {
  if (!dueDate) return 0;
  const due = new Date(`${dueDate}T00:00:00`).getTime();
  const now = new Date(`${today}T00:00:00`).getTime();
  if (Number.isNaN(due) || Number.isNaN(now)) return 0;
  return Math.floor((now - due) / 86_400_000);
}

/** Fälligkeits-Staffel wie in der Vorlage. Grundlage: Überfälligkeitstage zum Stichtag. */
function buildAging(items: readonly { readonly dueDate: string | null; readonly openAmount: number }[], today: string): readonly AgingBucket[] {
  const buckets = [
    { label: 'nicht fällig', min: -Infinity, max: 0 },
    { label: '1 bis 40 Tage', min: 1, max: 40 },
    { label: '41 bis 70 Tage', min: 41, max: 70 },
    { label: '71 bis 100 Tage', min: 71, max: 100 },
    { label: '101 bis 180 Tage', min: 101, max: 180 },
    { label: '181 Tage und mehr', min: 181, max: Infinity },
  ];
  return buckets.map((bucket) => {
    const rows = items.filter((item) => {
      const overdue = overdueDays(item.dueDate, today);
      return overdue >= bucket.min && overdue <= bucket.max;
    });
    return { label: bucket.label, count: rows.length, amount: rows.reduce((sum, item) => sum + item.openAmount, 0) };
  });
}

function groupBy(
  items: readonly (OpListItem & { readonly partnerNumber: string; readonly partnerName: string })[],
  contacts?: OpListPartnerContacts,
): readonly OpListGroup[] {
  const map = new Map<string, { partnerNumber: string; partnerName: string; items: OpListItem[] }>();
  for (const item of items) {
    const key = item.partnerNumber || item.partnerName;
    const existing = map.get(key);
    if (existing) existing.items.push(item);
    else map.set(key, { partnerNumber: item.partnerNumber, partnerName: item.partnerName, items: [item] });
  }
  return [...map.values()]
    .sort((a, b) => a.partnerNumber.localeCompare(b.partnerNumber, 'de'))
    .map((group) => {
      const contact = contacts?.get(group.partnerNumber);
      return {
        partnerNumber: group.partnerNumber,
        partnerName: group.partnerName,
        partnerCity: contact?.city ?? '',
        partnerPhone: contact?.phone ?? '',
        items: group.items,
        subtotal: group.items.reduce((sum, item) => sum + item.openAmount, 0),
      };
    });
}

export function buildReceivablesOpList(invoices: readonly Invoice[], today: string, contacts?: OpListPartnerContacts): OpListData {
  const open = invoices.filter((invoice) => invoice.paymentStatus === 'open' || invoice.paymentStatus === 'partial');
  const items = open.map((invoice) => ({
    partnerNumber: invoice.customerNumber,
    partnerName: invoice.customerName,
    bookingDate: invoice.issueDate ?? invoice.createdAt.slice(0, 10),
    documentNumber: invoice.invoiceNumber ?? invoice.proposalNumber,
    externalNumber: invoice.sourceNumber,
    description: invoice.customerName,
    dueDate: invoice.dueDate,
    dunningLevel: invoice.dunningLevel,
    openAmount: Math.round((invoice.grossTotal - invoice.paidAmount) * 100) / 100,
  }));
  return {
    groups: groupBy(items, contacts),
    grandTotal: items.reduce((sum, item) => sum + item.openAmount, 0),
    aging: buildAging(items, today),
  };
}

export function buildPayablesOpList(incoming: readonly IncomingInvoice[], today: string, contacts?: OpListPartnerContacts): OpListData {
  const open = incoming.filter((invoice) => !['paid', 'cancelled', 'rejected'].includes(invoice.status));
  const items = open.map((invoice) => ({
    partnerNumber: invoice.supplierNumber,
    partnerName: invoice.supplierName,
    bookingDate: invoice.invoiceDate,
    documentNumber: invoice.number,
    externalNumber: invoice.supplierInvoiceNumber,
    description: invoice.bookingText || invoice.supplierName,
    dueDate: invoice.dueDate,
    dunningLevel: 0,
    openAmount: Math.round(invoice.grossAmount * 100) / 100,
  }));
  return {
    groups: groupBy(items, contacts),
    grandTotal: items.reduce((sum, item) => sum + item.openAmount, 0),
    aging: buildAging(items, today),
  };
}

export function renderOpListHtml(kind: OpListKind, companyName: string, asOf: string, data: OpListData): string {
  const isDebitor = kind === 'debitor';
  const heading = isDebitor ? 'Debitor - Offene Posten' : 'Kreditor - Offene Posten';
  const partnerWord = isDebitor ? 'Debitor' : 'Kreditor';
  const cols = isDebitor
    ? ['Buchungsdatum', 'Beleg', 'Externe Nr.', 'Beschreibung', 'Fällig', 'Mahnstufe', 'Restbetrag']
    : ['Buchungsdatum', 'Beleg', 'Externe Nr.', 'Beschreibung', 'Fällig', 'Restbetrag'];

  const groupRows = data.groups.map((group) => {
    const itemRows = group.items.map((item) => `
      <tr>
        <td>${dateShort(item.bookingDate)}</td>
        <td>${esc(item.documentNumber)}</td>
        <td>${esc(item.externalNumber)}</td>
        <td class="op-desc">${esc(item.description)}</td>
        <td>${dateShort(item.dueDate)}</td>
        ${isDebitor ? `<td class="op-center">${dunningText(item.dunningLevel)}</td>` : ''}
        <td class="op-num">${money(item.openAmount)} EUR</td>
      </tr>`).join('');
    return `
      <tr class="op-group">
        <td class="op-partner" colspan="${cols.length - 1}"><strong>${esc(group.partnerNumber)}</strong> &nbsp; ${esc(group.partnerName)}${contactLine(group.partnerCity, group.partnerPhone)}</td>
        <td></td>
      </tr>
      ${itemRows}
      <tr class="op-subtotal">
        <td colspan="${cols.length - 1}"></td>
        <td class="op-num">${money(group.subtotal)} EUR</td>
      </tr>`;
  }).join('');

  const agingRows = data.aging.map((bucket) => `
    <tr>
      <td>${bucket.label}:</td>
      <td class="op-center">${bucket.count || ''}</td>
      <td class="op-num">${money(bucket.amount)}</td>
    </tr>`).join('');

  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${partnerWord}-OP-Liste ${esc(companyName)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; padding: 14mm 12mm; font-size: 10px; }
  .op-head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1.5px solid #111; padding-bottom: 4px; }
  .op-head h1 { font-size: 15px; margin: 6px 0 0; }
  .op-head .op-meta { text-align: right; color: #444; }
  .op-sub { margin: 3px 0 10px; color: #444; }
  table.op { width: 100%; border-collapse: collapse; }
  table.op th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: .03em; color: #555; border-bottom: 1px solid #999; padding: 4px 6px; }
  table.op td { padding: 3px 6px; vertical-align: top; }
  table.op th:last-child, .op-num { text-align: right; }
  .op-center { text-align: center; }
  .op-desc { color: #333; }
  tr.op-group td { border-top: 1px solid #ccc; padding-top: 8px; }
  tr.op-group .op-partner { font-size: 11px; }
  .op-partner-contact { color: #666; font-weight: 400; font-size: 10px; }
  tr.op-subtotal td { border-top: 1px solid #ddd; font-weight: 700; }
  tr.op-total td { border-top: 2px solid #111; border-bottom: 2px solid #111; font-size: 12px; font-weight: 800; padding: 6px; }
  .op-aging { margin-top: 16px; margin-left: auto; width: 320px; }
  .op-aging h3 { font-size: 10px; text-transform: uppercase; color: #555; margin: 0 0 4px; border-bottom: 1px solid #999; padding-bottom: 3px; }
  .op-aging table { width: 100%; border-collapse: collapse; }
  .op-aging td { padding: 2px 4px; }
  @media print { body { padding: 0; } @page { size: A4 portrait; margin: 12mm; } }
</style></head><body>
  <div class="op-head">
    <div><strong>${esc(companyName)}</strong><h1>${heading}</h1></div>
    <div class="op-meta">Stand am ${dateShort(asOf)}<br>${data.groups.length} ${partnerWord}en</div>
  </div>
  <p class="op-sub">Offene Posten zum Stichtag ${dateShort(asOf)}.</p>
  <table class="op">
    <thead><tr>${cols.map((c) => `<th>${c}</th>`).join('')}</tr></thead>
    <tbody>
      ${groupRows || `<tr><td colspan="${cols.length}">Keine offenen Posten.</td></tr>`}
      <tr class="op-total"><td colspan="${cols.length - 1}">Total EUR</td><td class="op-num">${money(data.grandTotal)}</td></tr>
    </tbody>
  </table>
  <div class="op-aging">
    <h3>Summe Fälligkeiten</h3>
    <table>
      <thead><tr><td></td><td class="op-center"><strong>Posten</strong></td><td class="op-num"><strong>Betrag</strong></td></tr></thead>
      <tbody>${agingRows}</tbody>
    </table>
  </div>
</body></html>`;
}

/** Öffnet die gerenderte OP-Liste in einem eigenen Tab (Fallback: neues Fenster). Strg+P druckt sie. */
export function openOpList(html: string): void {
  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}
